import { BurgerButton, DrawerOverlay, DrawerPanel, MainRow, StyledBurger, StyledLink, StyledNav } from './styles';
import { Col, Row } from 'react-styled-flexboxgrid';
import React, { useEffect, useState } from 'react';

import Link from 'next/link';
import Menu from '@/components/Nav/Menu';
import { NewLogo } from '@/components/ui/icons/Logo';

function ignoreEvent(event) {
  return event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift');
}

function isScrolledPastMain() {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.scrollY < 300;
}

interface NavBarProps {
  fixed?: boolean;
  isHomepage?: boolean;
  social?: unknown;
  menuData?: unknown;
  footer?: unknown;
  isDark?: boolean;
  noMargin?: boolean;
}

const NavBar: React.FC<NavBarProps> = ({
  fixed = true,
  isHomepage = false,
  noMargin,
  social,
  menuData = {},
  footer,
}) => {
  const [expanded, setExpanded] = useState(false);
  const { solutions, work, company } = menuData as {
    solutions: unknown[];
    work: unknown[];
    company: unknown[];
  };
  const [isTransparent, setIsTransparent] = useState(isHomepage);

  const openDrawer = (event) => {
    if (!ignoreEvent(event)) {
      setExpanded(true);
    }
  };

  const closeDrawer = (event) => {
    if (!ignoreEvent(event)) {
      setExpanded(false);
    }
  };

  useEffect(() => {
    if (isHomepage && typeof window !== 'undefined') {
      const onScroll = () => {
        setIsTransparent(isScrolledPastMain());
      };

      window.addEventListener('scroll', onScroll);

      return () => window.removeEventListener('scroll', onScroll);
    }
  }, [isHomepage]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setExpanded(false);
      }
    };

    if (expanded) {
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', onKeyDown);
    }

    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [expanded]);

  return (
    <StyledNav
      role="navigation"
      aria-label="main-navigation"
      fixed={fixed}
      noMargin={noMargin}
      isTransparent={isTransparent}
    >
      <MainRow middle="xs" between="xs" isTransparent={isTransparent}>
        <Col xs={10}>
          <Link href="/" passHref legacyBehavior>
            <StyledLink title="Foxbox Home" isTransparent={isTransparent}>
              <NewLogo title="Foxbox Digital Logo" height="60" />
            </StyledLink>
          </Link>
        </Col>
        <Col xs={2}>
          <Row end="xs" style={{ textAlign: 'right' }}>
            <Col xs={12} sm={12}>
              <BurgerButton onClick={openDrawer} isTransparent={isTransparent} aria-label="Open navigation menu">
                <StyledBurger />
              </BurgerButton>
            </Col>
          </Row>
        </Col>
        <DrawerOverlay $open={expanded} onClick={closeDrawer} aria-hidden={!expanded} />
        <DrawerPanel $open={expanded} role="dialog" aria-modal="true" aria-hidden={!expanded}>
          {expanded && (
            <Menu
              onClose={() => setExpanded(false)}
              isTransparent={isTransparent}
              social={social}
              footer={footer}
              companyList={company}
              solutionsList={solutions}
              workList={work}
            />
          )}
        </DrawerPanel>
      </MainRow>
    </StyledNav>
  );
};

// TODO: remove once we use the new navbar
// const useOldNavBar = false

// const Nav = (props) => !useOldNavBar ? <Navbar {...props} /> : <OldNavbar {...props} />
export default NavBar;
